import type { ChangeEvent, FocusEvent } from 'react';
import { useSelector } from 'react-redux';
import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { Box, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import type { SetFieldValue } from '../../types';
import { Label, TextArea } from '../AirAmbulanceFormStandalone.styles';
import { GROUND_CONTACT, PATIENT_INFO } from '../constants';
import { REQUIRED_ERROR } from '../validation';

interface FormValues {
  ground_contact: string;
  patient_info: string;
}

interface TouchedFields {
  ground_contact?: boolean;
  patient_info?: boolean;
}

interface ContactSectionProps {
  values: FormValues;
  touched: TouchedFields;
  handleBlur: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  setFieldValue: SetFieldValue;
  setFieldTouched: (field: string, touched: boolean) => void;
}

const ContactSection = ({
  values,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  setFieldTouched,
}: ContactSectionProps): JSX.Element => {
  const existingRequest = useSelector(selectExistingRequest);

  return (
    <>
      <Box pb={3}>
        <Label>
          <Text size="small" color="primaryText">
            Ground Contact Radio Channel & Unit Name*
          </Text>
        </Label>
        <InputText
          shouldErrorTakeSpace
          id={GROUND_CONTACT}
          name={GROUND_CONTACT}
          type="text"
          setFieldValue={setFieldValue}
          value={values.ground_contact}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleChange(e);
            setFieldTouched(GROUND_CONTACT, true);
          }}
          onBlur={handleBlur}
          error={
            touched.ground_contact && values.ground_contact.length === 0
              ? REQUIRED_ERROR
              : null
          }
          style={{ fontSize: '14px' }}
        />
      </Box>

      <Box pb={3}>
        <Label>
          <Text size="small" color="primaryText">
            Incident/Patient Info*
          </Text>
        </Label>
        <TextArea
          id={PATIENT_INFO}
          name={PATIENT_INFO}
          value={values.patient_info}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            handleChange(e);
            setFieldTouched(PATIENT_INFO, true);
          }}
          disabled={!!existingRequest}
          onBlur={handleBlur}
          placeholder="Additional Information for Responders"
          rows={4}
          style={{ fontSize: '14px' }}
        />
        {touched.patient_info && values.patient_info.length === 0 && (
          <ErrorWrapper error={REQUIRED_ERROR} />
        )}
      </Box>
    </>
  );
};

export default ContactSection;
